import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Observable } from 'rxjs';
import { realDateValidator } from 'src/app/utils/form-validator';

@Component({
  selector: 'app-settings-schedule-modal',
  templateUrl: './settings-schedule-modal.component.html',
  styleUrls: ['./settings-schedule-modal.component.scss'],
})
/**
 * Component for displaying reward overview form
 */
export class SettingsScheduleModalComponent implements OnInit {
  form = new FormGroup({
    pointExpirationPeriodMonths: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(99),
        Validators.pattern(/^[1-9]\d*$/),
      ],
      nonNullable: false,
    }),
    pointsExpirationEffectiveDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
  });
  minDate: Date = (() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  })();
  loading = false;
  hasChanges = false;
  labels = {
    startsFrom: 'starts-from',
    cancel: 'cancel',
    confirm: 'confirm',
    monthLabel: 'settings-schedule-modal.month-label',
    savingError: 'unexpected-error',
    successfullyMsg: 'system-settings-success-save',
    invalidDate: 'invalid-calendar-date',
  };

  /**
   * Constructor
   */
  constructor(
    public dialogRef: MatDialogRef<SettingsScheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      currentSettings: any;
      systemSettings: any;
      formsDisabled: boolean;
    },
    private readonly translationService: TranslationService,
    private readonly userService: UserService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * NgOnInit
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);

    const effectiveDateControl = this.form.get(
      'pointsExpirationEffectiveDate'
    )!;
    effectiveDateControl.addValidators(realDateValidator('invalidDate'));

    if (this.data?.systemSettings?.id) {
      const [year, month, day] =
        this.data.systemSettings.pointsExpirationEffectiveDate
          .split('-')
          .map(Number);
      const localDate = new Date(year, month - 1, day);
      this.form.setValue({
        pointExpirationPeriodMonths:
          this.data.systemSettings.pointExpirationPeriodMonths,
        pointsExpirationEffectiveDate: localDate,
      });
      this.form.valueChanges.subscribe(() => {
        this.hasChanges = this.compareIfHadChanges();
      });
    }
    effectiveDateControl.updateValueAndValidity();
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close(MatDialogResponse.FAIL);
  }

  /**
   * Main action to save a system settings schedule
   */
  saveSchedule() {
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    const formData = {
      pointExpirationPeriodMonths: this.form.value.pointExpirationPeriodMonths,
      fixedExpirationDate: false,
      pointsExpirationEffectiveDate:
        this.form.value.pointsExpirationEffectiveDate,
    };

    let request$: any;
    if (this.data?.systemSettings?.id) {
      request$ = this.userService.updateScheduledSystemSettings(
        this.data.systemSettings.id,
        formData
      );
    } else {
      request$ = this.userService.updateSystemSettings(formData);
    }

    this.executeScheduleUpdate(request$);
  }

  /**
   * Execute schedule update
   * @param {Observable<any>} request
   */
  private executeScheduleUpdate(request: Observable<any>) {
    request.subscribe({
      next: () => {
        this.loading = false;
        this.snackBarService.success(this.labels.successfullyMsg);
        this.dialogRef.close(MatDialogResponse.SUCCESS);
      },
      error: (err) => {
        this.loading = false;
        const msg = err?.message || this.labels.savingError;
        this.snackBarService.error(msg);
      },
    });
  }

  /**
   * Compare if the system settings values were changed since the last update
   * @return {boolean} Boolean indicating if the system settings changed
   */
  compareIfHadChanges(): boolean {
    if (!this.data?.systemSettings?.id) {
      return false;
    }

    if (
      Number(this.data.systemSettings.pointExpirationPeriodMonths) !==
      Number(this.form.value.pointExpirationPeriodMonths)
    ) {
      return true;
    }

    const formDateRaw: any = this.form.value.pointsExpirationEffectiveDate;

    if (formDateRaw) {
      const formDate: Date = formDateRaw._d ? formDateRaw._d : formDateRaw;

      const year = formDate.getFullYear();
      const month = String(formDate.getMonth() + 1).padStart(2, '0');
      const day = String(formDate.getDate()).padStart(2, '0');
      const formDateStr = `${year}-${month}-${day}`;
      if (
        this.data.systemSettings.pointsExpirationEffectiveDate !== formDateStr
      ) {
        return true;
      }
    }
    return false;
  }
}
